
/**
 * Utility functions for diagnostics features
 */

import { toast } from "sonner";

// Import our custom types to avoid conflicts with built-in types
import type { ErrorEntry, PerformanceEntry, ErrorReportingService, PerformanceMonitoringService } from '../types';

// Enable diagnostics based on URL params or localStorage setting
export const isDiagnosticsEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Check URL params
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('diagnostics')) {
    const setting = urlParams.get('diagnostics') !== 'false';
    localStorage.setItem('diagnostics_enabled', String(setting));
    return setting;
  }
  
  // Check localStorage
  return localStorage.getItem('diagnostics_enabled') === 'true';
};

// Check if we're in a development environment
export const isDevelopment = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return process.env.NODE_ENV === 'development' || 
    window.location.hostname === 'localhost' || 
    window.location.hostname.includes('staging');
};

// Initialize error reporting service
export const initErrorReportingService = (): void => {
  if (typeof window === 'undefined' || window._errorReporting) return;

  const errors: ErrorEntry[] = [];
  
  window._errorReporting = {
    getErrors: () => [...errors],
    clearErrors: () => errors.splice(0, errors.length),
    addError: (error: Error | string, componentName?: string, severity: 'error' | 'warning' | 'info' = 'error', additionalData?: Record<string, any>) => {
      const errorEntry: ErrorEntry = {
        message: typeof error === 'string' ? error : error.message,
        stack: typeof error !== 'string' ? error.stack : undefined,
        timestamp: new Date(),
        componentName,
        severity,
        additionalData
      };
      
      errors.push(errorEntry);
      
      // Only show toast in development or when diagnostics is enabled
      if (isDevelopment() || isDiagnosticsEnabled()) {
        if (severity === 'error') {
          toast.error(`Error in ${componentName || 'application'}: ${errorEntry.message}`);
        } else if (severity === 'warning') {
          toast.warning(`Warning in ${componentName || 'application'}: ${errorEntry.message}`);
        }
      }
      
      // Also log to console for development
      if (isDevelopment()) {
        console.error(`[${severity.toUpperCase()}] ${componentName ? `[${componentName}] ` : ''}${errorEntry.message}`, error);
      }
    }
  } as ErrorReportingService;
};

// Initialize performance monitoring service
export const initPerformanceMonitoringService = (): void => {
  if (typeof window === 'undefined' || window._performanceMonitoring) return;
  
  const metrics: PerformanceEntry[] = [];
  
  window._performanceMonitoring = {
    getMetrics: () => [...metrics],
    recordMetric: (name: string, value: number) => {
      metrics.push({
        metricName: name,
        value,
        timestamp: new Date()
      });
    },
    clearMetrics: () => metrics.splice(0, metrics.length)
  } as PerformanceMonitoringService;
};

// Initialize all diagnostic services
export const initDiagnosticServices = (): void => {
  initErrorReportingService();
  initPerformanceMonitoringService();
};
